import { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { userService } from '@/services/userService';

interface ProfileCompletionStatus {
  percentage: number;
  missingFields: string[];
  isComplete: boolean;
}

export const useProfileCompletion = () => {
  const { currentUser, accountType } = useUser();
  const [completionStatus, setCompletionStatus] = useState<ProfileCompletionStatus>({
    percentage: 0,
    missingFields: [],
    isComplete: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!currentUser?.user_id || !accountType) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const profile = await userService.getProfileByUserId(currentUser.user_id);
        
        if (!profile) {
          setCompletionStatus({
            percentage: 0,
            missingFields: ['Profile not found'],
            isComplete: false
          });
          return;
        }

        let totalFields = 0;
        let completedFields = 0;
        let missingFields: string[] = [];

        // Check basic profile fields
        const basicFields = [
          { field: 'display_name', label: 'Display Name' },
          { field: 'bio', label: 'Bio' },
          { field: 'location', label: 'Location' },
        ];

        basicFields.forEach(({ field, label }) => {
          totalFields++;
          if (profile[field]) {
            completedFields++;
          } else {
            missingFields.push(label);
          }
        });

        // Check account-specific fields
        switch (accountType) {
          case 'talent':
            if (profile.talent) {
              const talentFields = [
                { field: 'name', label: 'Name' },
                { field: 'sport', label: 'Sport' },
                { field: 'achievements', label: 'Achievements' },
              ];

              talentFields.forEach(({ field, label }) => {
                totalFields++;
                const value = (profile.talent as any)[field];
                if (value && (Array.isArray(value) ? value.length > 0 : true)) {
                  completedFields++;
                } else {
                  missingFields.push(label);
                }
              });
            } else {
              totalFields += 3;
              missingFields.push('Name', 'Sport', 'Achievements');
            }
            break;

          case 'agent':
            if (profile.agent) {
              const agentFields = [
                { field: 'professional_name', label: 'Professional Name' },
                { field: 'agency_name', label: 'Agency Name' },
                { field: 'specialization', label: 'Specialization' },
                { field: 'professional_bio', label: 'Professional Bio' },
              ];

              agentFields.forEach(({ field, label }) => {
                totalFields++;
                const value = profile.agent[field];
                if (value && (Array.isArray(value) ? value.length > 0 : true)) {
                  completedFields++;
                } else {
                  missingFields.push(label);
                }
              });
            } else {
              totalFields += 4;
              missingFields.push('Professional Name', 'Agency Name', 'Specialization', 'Professional Bio');
            }
            break;

          case 'brand':
            if (profile.brand) {
              const brandFields = [
                { field: 'company_name', label: 'Company Name' },
                { field: 'industry', label: 'Industry' },
                { field: 'description', label: 'Description' },
                { field: 'marketing_objectives', label: 'Marketing Objectives' },
              ];

              brandFields.forEach(({ field, label }) => {
                totalFields++;
                const value = profile.brand[field];
                if (value && (Array.isArray(value) ? value.length > 0 : true)) {
                  completedFields++;
                } else {
                  missingFields.push(label);
                }
              });
            } else {
              totalFields += 4;
              missingFields.push('Company Name', 'Industry', 'Description', 'Marketing Objectives');
            }
            break;

          case 'venture':
            if (profile.venture_capital) {
              const vcFields = [
                { field: 'firm_name', label: 'Firm Name' },
                { field: 'investment_focus', label: 'Investment Focus' },
                { field: 'stage_preference', label: 'Stage Preference' },
                { field: 'investment_thesis', label: 'Investment Thesis' },
              ];

              vcFields.forEach(({ field, label }) => {
                totalFields++;
                const value = profile.venture_capital[field];
                if (value && (Array.isArray(value) ? value.length > 0 : true)) {
                  completedFields++;
                } else {
                  missingFields.push(label);
                }
              });
            } else {
              totalFields += 4;
              missingFields.push('Firm Name', 'Investment Focus', 'Stage Preference', 'Investment Thesis');
            }
            break;
        }

        const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
        const isComplete = percentage >= 80; // Consider 80% as "complete"

        setCompletionStatus({
          percentage,
          missingFields,
          isComplete
        });

      } catch (error) {
        console.error('Error checking profile completion:', error);
        setCompletionStatus({
          percentage: 0,
          missingFields: ['Error loading profile'],
          isComplete: false
        });
      } finally {
        setLoading(false);
      }
    };

    checkProfileCompletion();
  }, [currentUser?.user_id, accountType]);

  return { completionStatus, loading };
};